import { useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { Clock, CaretDown, Timer, Download, ArrowsCounterClockwise } from "@phosphor-icons/react";

const fmtHm = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};

const fmtDuration = (minutes) => {
  const m = Math.max(0, Math.round(minutes || 0));
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h === 0) return `${rest}m`;
  if (rest === 0) return `${h}h`;
  return `${h}h${String(rest).padStart(2, "0")}`;
};

const isoToday = () => new Date().toISOString().slice(0, 10);
const isoMonday = () => {
  const d = new Date();
  const day = (d.getDay() + 6) % 7; // 0=Mon .. 6=Sun
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
};

const API = BACKEND_URL;

/**
 * Per-tech daily/weekly hours card. Toggle Jour / Semaine + CSV export.
 */
export default function TechHoursCard() {
  const [mode, setMode] = useState("day"); // "day" | "week"
  const [date, setDate] = useState(isoToday());
  const [weekStart, setWeekStart] = useState(isoMonday());
  const [dayRows, setDayRows] = useState([]);
  const [weekData, setWeekData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    if (mode === "day") {
      api.get("/admin/tech-hours", { params: { date } })
        .then((r) => { if (alive) setDayRows(r.data.entries || []); })
        .catch(() => { if (alive) setDayRows([]); })
        .finally(() => { if (alive) setLoading(false); });
    } else {
      api.get("/admin/tech-hours/week", { params: { start: weekStart } })
        .then((r) => { if (alive) setWeekData(r.data); })
        .catch(() => { if (alive) setWeekData(null); })
        .finally(() => { if (alive) setLoading(false); });
    }
    return () => { alive = false; };
  }, [mode, date, weekStart]);

  const dayWorked = dayRows.filter((r) => r.total_min > 0);
  const dayTotal = dayWorked.reduce((s, r) => s + (r.total_min || 0), 0);
  const dayActive = dayRows.filter((r) => r.has_active_timer).length;

  const weekTotal = (weekData?.totals || []).reduce((s, t) => s + (t.total_min || 0), 0);

  const headerLabel =
    mode === "day"
      ? `${fmtDuration(dayTotal)} cumulées · ${dayWorked.length} ${dayWorked.length > 1 ? "techs actifs" : "tech actif"}`
      : weekData
        ? `${fmtDuration(weekTotal)} cumulées · ${weekData.totals.length} tech${weekData.totals.length > 1 ? "s" : ""}`
        : "…";

  const onExport = () => {
    const params = mode === "week" ? `start=${weekStart}` : `date=${date}`;
    // Use fetch to force download since HttpOnly cookie is auto-included
    fetch(`${API}/api/admin/tech-hours/export?${params}`, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = mode === "week" ? `heures_semaine_${weekStart}.csv` : `heures_${date}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((e) => console.warn("[tech-hours] export failed:", e?.message || e));
  };

  const prevWeek = () => {
    const d = new Date(weekStart); d.setDate(d.getDate() - 7);
    setWeekStart(d.toISOString().slice(0, 10));
  };
  const nextWeek = () => {
    const d = new Date(weekStart); d.setDate(d.getDate() + 7);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid="tech-hours-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-4 active:bg-white/5 transition-colors text-left"
        data-testid="tech-hours-toggle"
      >
        <div className="w-9 h-9 rounded-xl bg-[#005CFF]/20 border border-[#005CFF]/30 flex items-center justify-center shrink-0">
          <Clock size={18} weight="duotone" className="text-[#7CB2FF]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-white/50">Heures {mode === "day" ? "du jour" : "de la semaine"}</div>
          <div className="text-sm font-semibold mt-0.5 flex items-center gap-2 flex-wrap">
            {headerLabel}
            {dayActive > 0 && mode === "day" && (
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-normal">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {dayActive} en cours
              </span>
            )}
          </div>
        </div>
        <CaretDown size={16} className={`text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-white/5">
          {/* Controls row */}
          <div className="flex items-center gap-2 px-4 py-2 bg-white/[0.02] flex-wrap">
            <div className="flex rounded-lg border border-white/10 overflow-hidden" data-testid="tech-hours-mode">
              <button
                onClick={() => setMode("day")}
                className={`px-3 py-1 text-[11px] font-semibold ${mode === "day" ? "bg-[#005CFF] text-white" : "text-white/60"}`}
                data-testid="tech-hours-mode-day"
              >Jour</button>
              <button
                onClick={() => setMode("week")}
                className={`px-3 py-1 text-[11px] font-semibold ${mode === "week" ? "bg-[#005CFF] text-white" : "text-white/60"}`}
                data-testid="tech-hours-mode-week"
              >Semaine</button>
            </div>
            {mode === "day" ? (
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bg-transparent border border-white/10 rounded px-2 py-1 text-[11px] text-white/80"
                data-testid="tech-hours-date"
              />
            ) : (
              <div className="flex items-center gap-1">
                <button onClick={prevWeek} className="w-7 h-7 rounded border border-white/10 text-white/60 hover:bg-white/5" data-testid="tech-hours-week-prev">‹</button>
                <input
                  type="date"
                  value={weekStart}
                  onChange={(e) => setWeekStart(e.target.value)}
                  className="bg-transparent border border-white/10 rounded px-2 py-1 text-[11px] text-white/80"
                  data-testid="tech-hours-week-start"
                />
                <button onClick={nextWeek} className="w-7 h-7 rounded border border-white/10 text-white/60 hover:bg-white/5" data-testid="tech-hours-week-next">›</button>
              </div>
            )}
            <div className="ml-auto flex items-center gap-2">
              {loading && <ArrowsCounterClockwise size={12} className="text-white/40 animate-spin" />}
              <button
                onClick={onExport}
                className="text-[11px] px-2 py-1 rounded border border-white/10 text-white/70 hover:bg-white/5 flex items-center gap-1"
                data-testid="tech-hours-export"
              >
                <Download size={12} /> CSV
              </button>
            </div>
          </div>

          {mode === "day" ? (
            dayRows.length === 0 ? (
              <div className="p-6 text-center text-xs text-white/40" data-testid="tech-hours-empty">Aucun pointage ce jour</div>
            ) : (
              <div className="divide-y divide-white/5">
                {dayRows.map((r) => <TechRow key={r.user_id} row={r} />)}
              </div>
            )
          ) : (
            <WeekView data={weekData} loading={loading} />
          )}
        </div>
      )}
    </div>
  );
}

function TechRow({ row }) {
  const worked = row.total_min > 0;
  const bureauArr = row.depot_arrived_at;
  const bureauDep = row.depot_left_at;
  const chantierStart = row.first_start;
  const chantierEnd = row.last_stop;
  // Total time bureau→bureau (if both known), else clocked chantier minutes
  let totalMin = row.total_min || 0;
  if (bureauArr && bureauDep) {
    try {
      const diff = (new Date(bureauDep) - new Date(bureauArr)) / 60000;
      if (diff > totalMin) totalMin = diff;
    } catch (e) { console.debug("[tech-hours] bureau diff parse failed:", e?.message || e); }
  }
  return (
    <div className={`px-4 py-3 space-y-1.5 ${worked || bureauArr ? "" : "opacity-40"}`} data-testid={`tech-hours-row-${row.user_id}`}>
      <div className="flex items-center gap-3">
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: row.user_color }} />
        <div className="flex-1 min-w-0 flex items-center gap-1.5">
          <div className="text-sm font-medium truncate">{row.user_name}</div>
          {row.has_active_timer && <Timer size={11} weight="fill" className="text-emerald-400 shrink-0" />}
        </div>
        <div className={`font-mono tabular-nums font-semibold text-sm ${worked ? "text-white" : "text-white/40"}`}>
          {fmtDuration(totalMin)}
        </div>
      </div>
      {/* Bureau ↔ Chantier link — la base pour établir la feuille d'heures compta */}
      <div className="grid grid-cols-2 gap-2 pl-5">
        <div className={`rounded-lg px-2 py-1.5 border ${bureauArr ? "bg-[#7CB2FF]/10 border-[#7CB2FF]/25" : "bg-white/[0.02] border-white/5"}`}>
          <div className="text-[9px] uppercase tracking-widest text-white/40">Bureau</div>
          <div className="font-mono text-[11px] tabular-nums text-white/80">
            {fmtHm(bureauArr)} <span className="text-white/40">→</span> {fmtHm(bureauDep)}
          </div>
        </div>
        <div className={`rounded-lg px-2 py-1.5 border ${worked ? "bg-emerald-500/10 border-emerald-500/25" : "bg-white/[0.02] border-white/5"}`}>
          <div className="text-[9px] uppercase tracking-widest text-white/40">
            Chantier{row.intervention_count > 0 ? ` · ${row.intervention_count}` : ""}
          </div>
          <div className="font-mono text-[11px] tabular-nums text-white/80">
            {chantierStart && chantierStart !== bureauArr ? fmtHm(chantierStart) : "—"} <span className="text-white/40">→</span> {chantierEnd && chantierEnd !== bureauDep ? fmtHm(chantierEnd) : "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

function WeekView({ data, loading }) {
  if (loading && !data) return <div className="p-6 text-center text-xs text-white/40">Chargement…</div>;
  if (!data || (data.totals || []).length === 0) {
    return <div className="p-6 text-center text-xs text-white/40" data-testid="tech-hours-week-empty">Aucun pointage cette semaine</div>;
  }
  const dayLabels = ["L", "M", "M", "J", "V", "S", "D"];
  const dayDates = data.days.map((d) => d.date);
  return (
    <div className="overflow-x-auto" data-testid="tech-hours-week-table">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
            <th className="text-left px-3 py-2 font-normal">Tech</th>
            {dayLabels.map((l, i) => (
              <th key={dayDates[i]} className="text-center px-1 py-2 font-normal">
                <div>{l}</div>
                <div className="text-[9px] text-white/30 mt-0.5">{dayDates[i]?.slice(8, 10)}</div>
              </th>
            ))}
            <th className="text-right px-3 py-2 font-normal">Total</th>
          </tr>
        </thead>
        <tbody>
          {data.totals.map((t) => (
            <tr key={t.user_id} className="border-b border-white/5" data-testid={`week-row-${t.user_id}`}>
              <td className="px-3 py-2 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: t.user_color }} />
                  <span className="truncate">{t.user_name}</span>
                </div>
              </td>
              {dayDates.map((d) => {
                const mins = t.per_day?.[d] || 0;
                return (
                  <td key={d} className={`text-center px-1 py-2 font-mono tabular-nums ${mins > 0 ? "text-white" : "text-white/20"}`}>
                    {mins > 0 ? fmtDuration(mins) : "·"}
                  </td>
                );
              })}
              <td className="text-right px-3 py-2 font-mono tabular-nums font-semibold text-white">
                {fmtDuration(t.total_min)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
